import { runTransactionAttempts } from '../../../db/src/db'
import type { TursoDriverCallContext } from '../database-adapter'
import { createWorkerSqlQueue } from './turso-worker-driver'
import type { TursoDriver } from '@unni-craftsman/turso-fts'
import { proxy, type Remote } from 'comlink'
import type { ReplicaFailures } from '@unni-craftsman/turso-fts-common'
import type { SourceTransaction } from '../source-transaction'
import { createFailureClient, type FailureEvent } from './failure-commands'
import { copySourceBatch, validateSourceCallContext, deliverSourceCompletion } from './source-commands'
import type { DatabaseDriver, DriverCallContext } from '@w/db'
import { nullTel, type Tel } from '@w/tel'
import { disposeAll } from '@w/utils'
import type {
  TursoWorkerBootMetrics,
  TursoWorkerDriver,
  WorkerCallBaggage,
  WorkerCallEnvelope,
  WorkerReplicaState,
  WorkerReplicaEvent,
} from './turso-worker-driver'

export type TursoReplicaState = TursoDriver['state']

export function createTursoWorkerProxy(
  remote: Remote<TursoWorkerDriver>,
  opts: { concurrentTransactions?: boolean; tel: Tel; onInvalidated?: (ids: number[]) => void; onReplicaEvent?: (event: WorkerReplicaEvent) => void },
  ready: Promise<unknown> = Promise.resolve(),
): TursoWorkerDatabaseDriver & {
  subscribe(sql: string, onInvalidate: () => void): Promise<() => void>
  subscribeIds(sql: string): Promise<number>
  unsubscribeId(id: number): Promise<void>
  commitTx(): Promise<void>
  rollbackTx(): Promise<void>
  _replicaReady: Promise<number>
  readonly _replicaState: TursoReplicaState | undefined
} {
  let closed = false
  const callbacks = new Map<number, () => void>()
  const replicaSubscriptions = new Set<() => void>()
  const replicaListeners = new Set<(state: TursoReplicaState) => void>()
  let completionError: Error | undefined
  let replicaState: TursoReplicaState | undefined
  const failureEvents = new Set<(event: FailureEvent) => void>()
  // One callback port orders producer completion before actual native state delivery.
  const replicaReady = remote.subscribeReplica(proxy((event: WorkerReplicaEvent) => {
    if (!closed && event.failure) for (const listener of failureEvents) listener(event.failure)
    opts.onReplicaEvent?.(event)
    if (!opts.onReplicaEvent && event.completion) completionError = receiveReplicaCompletion(opts.tel, event.completion)
    if (closed || !event.state) return
    const state = event.state.error && completionError
      ? Object.freeze({ ...event.state, error: completionError })
      : deserializeReplicaState(event.state)
    completionError = undefined
    replicaState = state
    for (const listener of replicaListeners) listener(state)
  }))
  void replicaReady.catch(() => {})
  const dispatchInvalidated = (ids: number[]): void => {
    const notify = new Set<() => void>()
    for (const id of ids) {
      const callback = callbacks.get(id)
      if (callback) notify.add(callback)
    }
    for (const callback of notify) callback()
    opts.onInvalidated?.(ids)
  }
  const sinkReady = remote.setInvalidationSink(proxy(dispatchInvalidated))
  void sinkReady.catch(() => {})
  async function subscribeIds(sql: string): Promise<number> {
    if (closed) throw new Error('Turso worker database closed')
    await ready
    await sinkReady
    if (closed) throw new Error('Turso worker database closed')
    return remote.subscribe(sql)
  }
  async function unsubscribeId(id: number): Promise<void> {
    callbacks.delete(id)
    if (closed) return
    await remote.unsubscribe(id)
  }
  const defaultTel = opts.tel
  const runAfterTransactions = createWorkerSqlQueue()

  function callBaggage(tel: Tel): WorkerCallBaggage | null {
    if (tel === nullTel) return null
    const baggage = tel._getBaggage()
    return {
      baggage: baggage.baggage,
      parentId: baggage.parentId,
      sessionId: baggage.sessionId,
      traceFlags: baggage.traceFlags,
      traceId: baggage.traceId,
      traceState: baggage.traceState,
    }
  }

  function unwrap<T>(envelope: WorkerCallEnvelope<T>, tel: Tel): T {
    dispatchInvalidated(envelope.invalidated)
    if (envelope.telemetry && tel !== nullTel) {
      // INV-1: optional import cannot replace a database result or its transported error.
      try {
        tel._insertBoundaryTrace(
          envelope.telemetry.trace,
          envelope.telemetry.metrics,
          envelope.telemetry.resource,
        )
      } catch {
        // Required invalidation delivery remains outside this instrumentation-only boundary.
      }
    }
    if (envelope.ok) return envelope.result
    throw deserializeWorkerError(envelope.error)
  }

  async function call<T>(
    tel: Tel,
    invoke: (
      baggage: WorkerCallBaggage | null,
    ) => Promise<WorkerCallEnvelope<T>>,
  ): Promise<T> {
    await ready
    return unwrap(await invoke(callBaggage(tel)), tel)
  }

  const tx: Omit<DatabaseDriver, 'transaction'> = {
    query<T>(
      sql: string,
      params?: unknown[],
      ctx?: TursoDriverCallContext,
    ): Promise<T[]> {
      const tel = ctx?.tel ?? defaultTel
      return call(
        tel,
        (baggage) =>
          remote.txQuery(sql, params, baggage) as Promise<
            WorkerCallEnvelope<T[]>
          >,
      )
    },
    exec(
      sql: string,
      params?: unknown[],
      ctx?: TursoDriverCallContext,
    ): Promise<void> {
      const tel = ctx?.tel ?? defaultTel
      return call(tel, (baggage) => remote.txExec(sql, params, baggage))
    },
    copyFrom(): Promise<void> {
      return Promise.reject(new Error('Turso driver does not support copyFrom'))
    },
  }
  const { runRootTransactionNow } = createWorkerTransactions({
    tx,
    async begin(ctx) { await ready; unwrap(await remote.beginTxWithEnvelope({ exclusive: ctx?.exclusive }), ctx?.tel ?? defaultTel) },
    async commit(tel) { unwrap(await remote.commitTx(), tel) },
    async rollback(tel) { unwrap(await remote.rollbackTx(), tel) },
  }, defaultTel, !!opts.concurrentTransactions)

  const failures = createFailureClient(
    request => runAfterTransactions(() => call(defaultTel, () => remote.failureCommand(request))),
    (scope, id) => runAfterTransactions(async () => { await replicaReady; await call(defaultTel, () => remote.subscribeFailures(scope, id)) }),
    id => call(defaultTel, () => remote.unsubscribeFailures(id)),
    failureEvents,
  )
  return {
    failures,
    sourceTransaction: (scope, statements, context) => {
      const batch = copySourceBatch(scope, statements)
      const owned = { ...context }
      validateSourceCallContext(owned)
      const tel = owned.tel ?? defaultTel
      return runAfterTransactions(() => {
        if (closed) throw new Error('Turso worker database closed')
        return call(tel, async baggage => {
          const envelope = await remote.sourceTransaction(batch.scope, batch.statements, baggage, owned.exclusive)
          deliverSourceCompletion(envelope.source, owned)
          return envelope
        })
      })
    },
    concurrentTransactions: !!opts.concurrentTransactions,
    _replicaReady: replicaReady,
    get _replicaState() { return replicaState },
    async subscribeReplica(listener) {
      if (closed) throw new Error('Turso worker database closed')
      await ready
      let active = true
      await replicaReady
      replicaListeners.add(listener)
      const release = (): void => {
        if (!active) return
        active = false
        replicaSubscriptions.delete(release)
        replicaListeners.delete(listener)
      }
      if (closed) { release(); throw new Error('Turso worker database closed') }
      replicaSubscriptions.add(release)
      try {
        if (replicaState) listener(replicaState)
      } catch (error) {
        release()
        throw error
      }
      return release
    },
    async exchange(ctx?: DriverCallContext & { signal?: AbortSignal }) {
      await ready
      if (closed) throw new Error('Turso worker database closed')
      const id = crypto.randomUUID()
      const abort = () => { void remote.cancelExchange(id) }
      if (ctx?.signal?.aborted) throw ctx.signal.reason
      ctx?.signal?.addEventListener('abort',abort,{ once: true })
      const tel = ctx?.tel ?? defaultTel
      try { return unwrap(await remote.exchange(callBaggage(tel), id), tel) }
      finally { ctx?.signal?.removeEventListener('abort',abort) }
    },
    async resetReplica(identity: Uint8Array, ctx?: DriverCallContext) {
      await ready
      if (closed) throw new Error('Turso worker database closed')
      const tel = ctx?.tel ?? defaultTel
      return unwrap(await remote.resetReplica(identity, callBaggage(tel)), tel)
    },
    async setSessionUser(userId: string | null) {
      await ready
      if (closed) throw new Error('Turso worker database closed')
      unwrap(await remote.setSessionUser(userId), defaultTel)
    },
    async commitTx() { unwrap(await remote.commitTx(), defaultTel) },
    async rollbackTx() { unwrap(await remote.rollbackTx(), defaultTel) },
    subscribeIds,
    unsubscribeId,
    async subscribe(sql, onInvalidate) {
      const id = await subscribeIds(sql)
      if (closed) throw new Error('Turso worker database closed')
      callbacks.set(id, onInvalidate)
      let active = true
      return () => {
        if (!active || closed) return
        active = false
        void unsubscribeId(id)
      }
    },
    query<T>(
      sql: string,
      params?: unknown[],
      ctx?: TursoDriverCallContext,
    ): Promise<T[]> {
      const tel = ctx?.tel ?? defaultTel
      return runAfterTransactions(() => call(
        tel,
        (baggage) =>
          remote.query(sql, params, baggage) as Promise<
            WorkerCallEnvelope<T[]>
          >,
      ))
    },
    exec(
      sql: string,
      params?: unknown[],
      ctx?: TursoDriverCallContext,
    ): Promise<void> {
      const tel = ctx?.tel ?? defaultTel
      return runAfterTransactions(() => call(tel, (baggage) => remote.exec(sql, params, baggage)))
    },
    async close(): Promise<void> {
      closed = true
      for (const release of replicaSubscriptions) release()
      callbacks.clear()
      await disposeAll([
        () => failures.close(),
        async () => { unwrap(await remote.closeWithEnvelope(), defaultTel) },
        async () => { await ready.catch(() => {}); await remote.unsubscribeReplica(await replicaReady) },
      ], 'Turso failure subscriptions and worker cleanup failed')
    },
    runWorkerTask<TOutput>(
      moduleUrl: string,
      input: unknown,
      ctx?: TursoDriverCallContext,
    ): Promise<TOutput> {
      const tel = ctx?.tel ?? defaultTel
      return runAfterTransactions(() => call(
        tel,
        (baggage) =>
          remote.runTask(moduleUrl, input, baggage) as Promise<
            WorkerCallEnvelope<TOutput>
          >,
      ), true)
    },
    copyFrom(): Promise<void> {
      return Promise.reject(new Error('Turso driver does not support copyFrom'))
    },
    transaction<T>(
      fn: (txArg: DatabaseDriver) => Promise<T>,
      ctx?: TursoDriverCallContext,
    ): Promise<T> {
      return runTransactionAttempts(() => runAfterTransactions(() => runRootTransactionNow(fn, ctx), true), !!opts.concurrentTransactions && !ctx?.transactionRetryManaged)
    },
  }
}

export function createWorkerTransactions(
  transport: {
    tx: Omit<DatabaseDriver, 'transaction'>
    begin(ctx?: TursoDriverCallContext): Promise<void>
    commit(tel: Tel): Promise<void>
    rollback(tel: Tel): Promise<void>
  },
  defaultTel: Tel,
  concurrent: boolean,
) {
  let transactionTel: Tel | undefined
  let transactionExclusive = false
  let savepointSeq = 0
  const tx: DatabaseDriver = {
    ...transport.tx,
    query(sql, params, ctx?: TursoDriverCallContext) {
      return transport.tx.query(sql, params, { ...ctx, tel: ctx?.tel ?? transactionTel ?? defaultTel })
    },
    exec(sql, params, ctx?: TursoDriverCallContext) {
      return transport.tx.exec(sql, params, { ...ctx, tel: ctx?.tel ?? transactionTel ?? defaultTel })
    },
    async transaction(fn, ctx?: TursoDriverCallContext) {
      if (ctx?.exclusive && !transactionExclusive) throw new Error('Exclusive transaction cannot nest inside a concurrent transaction')
      const savepoint = `sp_${savepointSeq++}`
      const context = { ...ctx, tel: ctx?.tel ?? transactionTel ?? defaultTel }
      await tx.exec(`SAVEPOINT ${savepoint}`, undefined, context)
      try {
        const result = await fn(tx)
        await tx.exec(`RELEASE ${savepoint}`, undefined, context)
        return result
      } catch (error) {
        try { await tx.exec(`ROLLBACK TO ${savepoint}; RELEASE ${savepoint}`, undefined, context) } catch { /* Surface original transaction error. */ }
        throw error
      }
    },
  }
  async function runRootTransactionNow<T>(fn: (tx: DatabaseDriver) => Promise<T>, ctx?: TursoDriverCallContext): Promise<T> {
    await transport.begin(ctx)
    transactionExclusive = !!ctx?.exclusive || !concurrent
    transactionTel = ctx?.tel ?? defaultTel
    try {
      const result = await fn(tx)
      await transport.commit(transactionTel)
      return result
    } catch (error) {
      try { await transport.rollback(transactionTel) } catch { /* Surface original transaction error. */ }
      throw error
    } finally { transactionTel = undefined }
  }
  return { runRootTransactionNow }
}

export type TursoWorkerOwnership = { isOwner(): boolean; subscribe(listener: (owned: boolean) => void): () => void }

export type TursoWorkerDatabaseDriver = DatabaseDriver & {
  failures?: ReplicaFailures
  sourceTransaction?: SourceTransaction
  readonly concurrentTransactions?: boolean
  transaction<T>(fn: (tx: DatabaseDriver) => Promise<T>, ctx?: TursoDriverCallContext): Promise<T>
  _replicaReady?: Promise<number>
  readonly _replicaState?: TursoReplicaState
  subscribeReplica(listener: (state: TursoReplicaState) => void): Promise<() => void>
  exchange?(ctx?: DriverCallContext & { signal?: AbortSignal }): Promise<bigint>
  resetReplica?(identity: Uint8Array, ctx?: DriverCallContext): Promise<bigint>
  setSessionUser?(userId: string | null): Promise<void>
  subscribe(sql: string, onInvalidate: () => void): Promise<() => void>
  ownership?: TursoWorkerOwnership
  /**
   * Boot phase timings of the underlying worker, resolved once the worker is
   * configured (undefined for factory-configured workers or failed boots).
   * Also recorded as a `db.worker.boot` tel span when a tel is provided.
   */
  bootMetrics?: Promise<TursoWorkerBootMetrics | undefined>
  _callWorker?: (op: string, args: unknown[]) => Promise<unknown>
  beginTx?: (options?: { exclusive?: boolean }) => Promise<void>
  commitTx?: () => Promise<void>
  rollbackTx?: () => Promise<void>
  runWorkerTask<TOutput>(
    moduleUrl: string,
    input: unknown,
    ctx?: TursoDriverCallContext,
  ): Promise<TOutput>
  txExec?: (
    sql: string,
    params?: unknown[],
    ctx?: TursoDriverCallContext,
  ) => Promise<void>
  txQuery?: <T>(
    sql: string,
    params?: unknown[],
    ctx?: TursoDriverCallContext,
  ) => Promise<T[]>
}

/** INV-4: one completed producer envelope imports its entire tree before reporting its associated failure. */
export function receiveReplicaCompletion(tel: Tel, completion: NonNullable<WorkerReplicaEvent['completion']>): Error | undefined {
  const error = completion.error ? deserializeWorkerError(completion.error) : undefined
  if (tel === nullTel) return error
  try {
    if (completion.telemetry) {
      const payload = completion.telemetry
      tel._insertBoundaryTrace(payload.trace, payload.metrics, payload.resource, { preserveParent: true, error, updateExisting: true, dedupeBySpan: true })
    } else if (error) tel.error(error)
  } catch { /* Optional telemetry cannot replace an original database outcome. */ }
  return error
}

export { deserializeWorkerError } from './worker-error'
import { deserializeWorkerError } from './worker-error'

export function deserializeReplicaState(state: WorkerReplicaState): TursoReplicaState {
  return Object.freeze({ ...state, ...(state.error ? { error: deserializeWorkerError(state.error) } : {}) })
}
